// Minimal ActionCable-like singleton client using a single WebSocket
// - One connection per URL
// - Deduplicated subscriptions keyed by identifier JSON
// - Simple reconnect with backoff
// - Confirm handlers run on every confirmation, so a subscriber can catch up after a reconnect
//   (docs/REALTIME.md; useEventChannel builds on this)
// - A socket that has gone quiet is dropped and reconnected, as Rails' own client does: pages keep
//   no polling to fall back on

// How long a subscription nobody holds stays on the server, for a subscriber that comes straight back.
const UNSUBSCRIBE_DELAY_MS = 1000
// ActionCable pings every 3 s. A socket that has heard nothing for this long is half-open (a laptop
// that slept, a network that changed under it) and won't deliver again.
const STALE_AFTER_MS = 10_000
const STALE_CHECK_MS = 3_000

type MessageHandler = (payload: any) => void
type ConfirmHandler = () => void

interface Subscription {
  identifier: string
  handlers: Set<MessageHandler>
  confirmHandlers: Set<ConfirmHandler>
  refCount: number
  confirmed: boolean
}

class CableClient {
  private url: string
  private socket: WebSocket | null = null
  private connecting = false
  private reconnectAttempts = 0
  private maxBackoffMs = 5000
  private subs = new Map<string, Subscription>()
  private lastHeardAt = 0
  private monitor: ReturnType<typeof setInterval> | null = null

  constructor(url: string) {
    this.url = url
  }

  // The socket is gone (closed, or dropped as stale): every subscription is confirmed again once one is
  // back, which tells its subscribers to load what they may have missed.
  private handleClosed() {
    this.socket = null
    this.connecting = false
    this.stopMonitor()
    for (const sub of this.subs.values()) sub.confirmed = false
    if (this.subs.size > 0) {
      const backoff = Math.min(1000 * Math.pow(2, this.reconnectAttempts++), this.maxBackoffMs)
      setTimeout(() => this.connect(), backoff)
    }
  }

  private startMonitor() {
    this.stopMonitor()
    this.monitor = setInterval(() => this.checkStale(), STALE_CHECK_MS)
  }

  private stopMonitor() {
    if (this.monitor) clearInterval(this.monitor)
    this.monitor = null
  }

  /** Drops the socket if it has gone quiet (see STALE_AFTER_MS), reconnecting and resubscribing. */
  checkStale() {
    const socket = this.socket
    if (!socket || socket.readyState !== WebSocket.OPEN) return
    if (Date.now() - this.lastHeardAt <= STALE_AFTER_MS) return
    socket.onopen = null
    socket.onclose = null
    socket.onmessage = null
    socket.close()
    this.handleClosed()
  }

  private connect() {
    if (this.socket || this.connecting) return
    this.connecting = true
    try {
      // ActionCable expects the subprotocol to be set
      const ws = new WebSocket(this.url, ['actioncable-v1-json', 'actioncable-v1'])
      this.socket = ws

      ws.onopen = () => {
        this.connecting = false
        this.reconnectAttempts = 0
        this.lastHeardAt = Date.now()
        this.startMonitor()
        // Resubscribe all known identifiers
        for (const id of this.subs.keys()) {
          this.send({ command: 'subscribe', identifier: id })
        }
      }

      ws.onclose = () => this.handleClosed()

      ws.onerror = () => {
        // let onclose handle reconnect
      }

      ws.onmessage = (event) => {
        this.lastHeardAt = Date.now()
        try {
          const msg = JSON.parse(event.data)
          // Ignore pings and welcome
          if (msg.type === 'ping' || msg.type === 'welcome' || msg.type === 'disconnect') return
          if (msg.type === 'confirm_subscription' && msg.identifier) {
            const sub = this.subs.get(msg.identifier)
            if (sub) {
              sub.confirmed = true
              for (const h of sub.confirmHandlers) h()
            }
            return
          }
          if (msg.type === 'reject_subscription' && msg.identifier) {
            const sub = this.subs.get(msg.identifier)
            if (sub) {
              for (const h of sub.handlers) h({ type: 'reject_subscription' })
            }
            return
          }
          // Regular broadcast: { identifier, message }
          if (msg.identifier && msg.message) {
            const sub = this.subs.get(msg.identifier)
            if (sub) {
              for (const h of sub.handlers) h(msg.message)
            }
          }
        } catch (e) {
          // swallow parse errors
        }
      }
    } catch (e) {
      this.connecting = false
    }
  }

  // A new signed-in token means a new connection (the server reads it when the socket opens). The client stays
  // the same, so its subscriptions move across: the socket reconnects with the new URL and subscribes again.
  setUrl(url: string) {
    if (url === this.url) return
    this.url = url
    const old = this.socket
    if (!old && !this.connecting) return
    if (old) {
      old.onopen = null
      old.onclose = null
      old.onmessage = null
      old.close()
    }
    this.socket = null
    this.connecting = false
    this.reconnectAttempts = 0
    this.stopMonitor()
    for (const sub of this.subs.values()) sub.confirmed = false
    if (this.subs.size > 0) this.connect()
  }

  private send(data: any) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data))
    }
  }

  disconnect() {
    this.subs.clear()
    this.stopMonitor()
    if (this.socket) {
      this.socket.close()
      this.socket = null
    }
  }

  // `onConfirm` runs whenever the server confirms the subscription: now if it already has, and again after each
  // reconnect.
  subscribe(identifier: object, handler: MessageHandler, onConfirm?: ConfirmHandler) {
    const id = JSON.stringify(identifier)
    let sub = this.subs.get(id)
    if (!sub) {
      sub = { identifier: id, handlers: new Set(), confirmHandlers: new Set(), refCount: 0, confirmed: false }
      this.subs.set(id, sub)
      // Ensure connection and send subscribe
      this.connect()
      this.send({ command: 'subscribe', identifier: id })
    }
    sub.refCount += 1
    sub.handlers.add(handler)
    if (onConfirm) {
      sub.confirmHandlers.add(onConfirm)
      if (sub.confirmed) onConfirm()
    }

    // Return unsubscribe function
    return () => {
      const current = this.subs.get(id)
      if (!current) return
      current.handlers.delete(handler)
      if (onConfirm) current.confirmHandlers.delete(onConfirm)
      current.refCount -= 1
      if (current.refCount <= 0) {
        // Unsubscribe a moment later, so a subscriber that comes straight back (React StrictMode's
        // effect double-invoke, a remount) keeps the server's subscription. Sending unsubscribe and
        // subscribe back to back can leave none: ActionCable runs a connection's commands on a
        // worker pool, so the unsubscribe can land last, after the subscribe was confirmed.
        setTimeout(() => {
          if (this.subs.get(id) !== current || current.refCount > 0) return
          this.subs.delete(id)
          this.send({ command: 'unsubscribe', identifier: id })
        }, UNSUBSCRIBE_DELAY_MS)
        // Don't proactively close the socket here — closing it while it's still in
        // CONNECTING state generates a browser warning, and React StrictMode's
        // effect double-invoke triggers exactly that pattern. Let onclose handle cleanup.
      }
    }
  }
}

function cableUrl(token: string | null): string {
  const apiBase = (import.meta as any).env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1'
  const wsUrl = apiBase.replace('http', 'ws').replace('/api/v1', '') + '/cable'
  return token ? `${wsUrl}?token=${encodeURIComponent(token)}` : wsUrl
}

// A tab coming back into view, or the network coming back, may find the socket dead without the
// client's timer having run (background tabs' timers are throttled): the page's client checks at once.
let watchingPage = false
function watchPage() {
  if (watchingPage) return
  watchingPage = true
  const check = () => ((globalThis as any).__streambridge_cable as CableClient | undefined)?.checkStale()
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', check)
  if (typeof window !== 'undefined') window.addEventListener('online', check)
}

// The page's one client. It follows the signed-in token (AuthProvider calls this whenever the token changes):
// replacing the client would strand every subscription made on the old one.
export function getCable(): CableClient {
  watchPage()
  const w = (globalThis as any)
  const url = cableUrl(localStorage.getItem('sb_token'))
  const existing = w.__streambridge_cable as CableClient | undefined
  if (existing) {
    existing.setUrl(url)
    return existing
  }
  const client = new CableClient(url)
  w.__streambridge_cable = client
  return client
}
